const DB_NAME = 'teo_widget_outbound_images';
const DB_VERSION = 1;
const STORE_NAME = 'blobs';

/** In-memory fallback when IndexedDB is unavailable (tests, private mode). */
const memoryBlobs = new Map<string, Blob>();

function blobKey(scopeKey: string, clientId: string): string {
  return `${scopeKey}:${clientId}`;
}

function canUseIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error ?? new Error('IDB open failed'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

function idbPut(key: string, blob: Blob): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error ?? new Error('IDB put failed'));
        };
        tx.objectStore(STORE_NAME).put(blob, key);
      }),
  );
}

function idbGet(key: string): Promise<Blob | undefined> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const request = tx.objectStore(STORE_NAME).get(key);
        request.onsuccess = () => {
          db.close();
          resolve(request.result as Blob | undefined);
        };
        request.onerror = () => {
          db.close();
          reject(request.error ?? new Error('IDB get failed'));
        };
      }),
  );
}

function idbDelete(key: string): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error ?? new Error('IDB delete failed'));
        };
        tx.objectStore(STORE_NAME).delete(key);
      }),
  );
}

function blobToFile(blob: Blob, fileName: string, mimeType?: string): File {
  if (blob instanceof File && blob.name === fileName) return blob;
  return new File([blob], fileName, {
    type: mimeType || blob.type || 'application/octet-stream',
  });
}

export async function putOutboundImageBlob(
  scopeKey: string,
  clientId: string,
  file: File,
): Promise<void> {
  const key = blobKey(scopeKey, clientId);
  if (!canUseIndexedDb()) {
    memoryBlobs.set(key, file);
    return;
  }
  try {
    await idbPut(key, file);
  } catch {
    memoryBlobs.set(key, file);
  }
}

export async function getOutboundImageBlob(
  scopeKey: string,
  clientId: string,
  fileName?: string,
  mimeType?: string,
): Promise<File | null> {
  const key = blobKey(scopeKey, clientId);
  let blob: Blob | undefined;

  if (canUseIndexedDb()) {
    try {
      blob = await idbGet(key);
    } catch {
      blob = memoryBlobs.get(key);
    }
  } else {
    blob = memoryBlobs.get(key);
  }

  if (!blob) return null;
  return blobToFile(blob, fileName ?? `image-${clientId}`, mimeType);
}

export async function removeOutboundImageBlob(
  scopeKey: string,
  clientId: string,
): Promise<void> {
  const key = blobKey(scopeKey, clientId);
  memoryBlobs.delete(key);
  if (!canUseIndexedDb()) return;
  try {
    await idbDelete(key);
  } catch {
    // ignore
  }
}

/** Test helper — clears in-memory fallback store. */
export function clearOutboundImageMemoryStore(): void {
  memoryBlobs.clear();
}
